import React, { useEffect, useMemo, useState } from 'react';
import {
  RefreshCw, ChevronDown, ChevronRight, FileCheck, Inbox, Archive,
  CheckCircle2, XCircle, AlertTriangle, ExternalLink,
} from 'lucide-react';
import { Button, Pill, Card, EmptyState, Slideout } from '../../index';
import { Pagination, usePaginationState } from '../../../common/Pagination';
import { StatusBadge } from '../widgets/StatusBadge';
import { EvidenceUploader } from '../widgets/EvidenceUploader';
import {
  ccListAttestations, ccGetMyAttestationQueue, ccGetReviewQueue,
  ccSubmitAttestation, ccReviewAttestation, ccUpdateAttestation,
  ccGetEvidence,
} from '../../../../services/api';

/**
 * AttestationsSlideout — V2 Attestations workspace (Phase B1 / G314).
 *
 * Replaces the V1 ?legacy=1&tab=ATTESTATIONS bounce. Production-grade
 * rebuild of the full V1 Attestations tab:
 *
 *   - 3 sub-views (MY_QUEUE / PENDING_REVIEW / ARCHIVED) with counts
 *   - Table with: Task name | Framework | Control | Status | Assignee | Due date
 *   - Expandable rows show: description, evidence list, evidence
 *     uploader (file + URL), submit / review controls, rejection reason
 *   - "Submit evidence" workflow: review evidence → set status SUBMITTED
 *   - Reviewer workflow: Approve or Reject with reason
 *
 * Mounted in the V2 shell as a 720px-wide Slideout so the operator can
 * inspect and act without losing the dashboard context.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Function} props.addToast
 */
const VIEWS = [
  { id: 'queue',    label: 'My queue',         icon: Inbox },
  { id: 'review',   label: 'Pending review',   icon: FileCheck },
  { id: 'archived', label: 'Archived',         icon: Archive },
];

export function AttestationsSlideout({ isOpen, onClose, addToast, focusId }) {
  const [view, setView] = useState('queue');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ queue: 0, review: 0, archived: 0 });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());
  const pg = usePaginationState(10);

  const reload = async () => {
    setLoading(true);
    try {
      /* Load all three sub-views in parallel — counts drive the
       * sub-view nav badges, and the currently-selected view's items
       * are what we render. */
      const [queueR, reviewR, archivedR] = await Promise.all([
        ccGetMyAttestationQueue().catch(() => null),
        ccGetReviewQueue().catch(() => null),
        ccListAttestations({ status: 'APPROVED' }).catch(() => null),
      ]);
      const queueList    = normalizeList(queueR);
      const reviewList   = normalizeList(reviewR);
      const archivedList = normalizeList(archivedR);
      setCounts({ queue: queueList.length, review: reviewList.length, archived: archivedList.length });
      setItems(view === 'queue' ? queueList
             : view === 'review' ? reviewList
             : archivedList);
    } finally {
      setLoading(false);
    }
  };

  /* Reload when slideout opens OR when sub-view changes. */
  useEffect(() => {
    if (!isOpen) return;
    reload();
    /* eslint-disable-next-line */
  }, [isOpen, view]);

  /* Reset pagination on sub-view switch — the row population is fresh. */
  useEffect(() => { pg.setPage(0); /* eslint-disable-next-line */ }, [view]);

  const visible = pg.paginate(items);
  const toggleExpand = (id) => setExpanded((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  // Deep-link focus: the My Tasks inbox opens this slideout with ?attestation=<id> so the
  // operator lands on their specific pending task, not a list to scan. A task waiting on the
  // assignee lives in the 'queue' sub-view, so pin there, expand the target card, and page to
  // it. Runs once the queue has loaded and only while the id is present + still in the list —
  // if it has already been actioned (no longer queued) we leave the default view rather than
  // forcing an empty focus.
  useEffect(() => {
    if (!isOpen || !focusId || loading) return;
    if (view !== 'queue') { setView('queue'); return; }
    const idx = items.findIndex((t) => String(t.id) === String(focusId));
    if (idx < 0) return;
    setExpanded((s) => new Set(s).add(focusId));
    pg.setPage(Math.floor(idx / pg.pageSize));
    /* eslint-disable-next-line */
  }, [isOpen, focusId, loading, view, items]);

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={760}
      ariaLabel="Attestations workspace"
      header={
        <div>
          <div className="flex items-center gap-2 mb-2">
            <FileCheck size={18} className="text-v2-action" />
            <h2 className="text-[16px] font-bold text-v2-ink m-0">Attestations</h2>
            <Pill tone="cyan" size="sm">{counts.queue + counts.review} open</Pill>
          </div>
          <p className="text-[12.5px] text-v2-ink-secondary m-0">
            Evidence submission, executive sign-offs, and quarterly compliance reviews.
          </p>
          {/* Sub-view tab nav */}
          <div className="mt-3 flex items-center gap-1 border-b border-v2-border-subtle -mx-4 px-4">
            {VIEWS.map((v) => {
              const Icon = v.icon;
              const active = view === v.id;
              const count = counts[v.id];
              return (
                <button
                  key={v.id}
                  onClick={() => setView(v.id)}
                  aria-selected={active}
                  className={`px-3 py-2 text-[12.5px] font-medium border-b-2 -mb-px flex items-center gap-1.5 transition-colors ${
                    active
                      ? 'border-v2-action text-v2-action'
                      : 'border-transparent text-v2-ink-muted hover:text-v2-ink hover:border-v2-border'
                  }`}
                >
                  <Icon size={13} />
                  <span>{v.label}</span>
                  {count > 0 && (
                    <span className={`text-[10.5px] font-mono-v2 px-1.5 rounded-v2-pill ${
                      active ? 'bg-v2-action text-white' : 'bg-v2-muted text-v2-ink-muted'
                    }`}>{count}</span>
                  )}
                </button>
              );
            })}
            <Button variant="ghost" size="sm"
                    className="ml-auto"
                    iconLeft={<RefreshCw size={12} className={loading ? 'animate-spin' : ''} />}
                    onClick={reload}>
              Refresh
            </Button>
          </div>
        </div>
      }
    >
      {loading && items.length === 0 && (
        <p className="text-[12.5px] text-v2-ink-muted">Loading…</p>
      )}
      {!loading && items.length === 0 && (
        <EmptyState
          icon={<CheckCircle2 size={20} />}
          title={view === 'queue' ? 'Nothing in your queue' : view === 'review' ? 'No reviews pending' : 'No archived attestations'}
          description={view === 'queue'
            ? 'You have no attestations awaiting evidence submission.'
            : view === 'review'
              ? 'No attestations are currently waiting for your review.'
              : 'Approved attestations appear here once they\'re closed.'}
        />
      )}
      {items.length > 0 && (
        <ul className="space-y-2 m-0 p-0 list-none">
          {visible.map((a) => (
            <AttestationRow
              key={a.id}
              attestation={a}
              expanded={expanded.has(a.id)}
              onToggle={() => toggleExpand(a.id)}
              onSubmit={async (payload) => {
                const r = await ccSubmitAttestation(a.id, payload);
                if (r?.ok === false) throw new Error(r.error || 'Submit failed');
                addToast?.(`Submitted evidence for ${a.taskName || 'attestation'}.`, 'success');
                reload();
              }}
              onReview={async (decision, reason) => {
                const r = await ccReviewAttestation(a.id, { decision, reason });
                if (r?.ok === false) throw new Error(r.error || 'Review failed');
                addToast?.(`Attestation ${decision === 'APPROVE' ? 'approved' : 'rejected'}.`,
                           decision === 'APPROVE' ? 'success' : 'warn');
                reload();
              }}
              showReviewControls={view === 'review'}
              addToast={addToast}
            />
          ))}
        </ul>
      )}

      {items.length > 0 && (
        <div className="mt-3">
          <Pagination
            totalItems={items.length}
            page={pg.page}
            pageSize={pg.pageSize}
            onPageChange={pg.setPage}
            onPageSizeChange={pg.setPageSize}
            noun="attestations"
          />
        </div>
      )}
    </Slideout>
  );
}

/* ───── AttestationRow — expandable card with evidence + actions ───── */
function AttestationRow({ attestation: a, expanded, onToggle, onSubmit, onReview, showReviewControls, addToast }) {
  const [evidence, setEvidence] = useState(Array.isArray(a.evidence) ? a.evidence : []);
  const [loadingEvidence, setLoadingEvidence] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reviewMode, setReviewMode] = useState(null);   // null | 'approve' | 'reject'
  const [reason, setReason] = useState('');

  /* Lazy-load evidence on first expand to avoid fanning out to N endpoints
   * upfront. Cached after first fetch so re-expanding doesn't refetch. */
  useEffect(() => {
    if (!expanded || evidence.length > 0) return;
    setLoadingEvidence(true);
    ccGetEvidence(a.id)
      .then((r) => {
        const list = Array.isArray(r) ? r : (r?.data || r?.evidence || r?.items || []);
        setEvidence(Array.isArray(list) ? list : []);
      })
      .catch(() => { /* non-fatal — show empty */ })
      .finally(() => setLoadingEvidence(false));
  }, [expanded, a.id, evidence.length]);

  const handleSubmit = async () => {
    if (evidence.length === 0) {
      addToast?.('Add at least one piece of evidence before submitting.', 'warn');
      return;
    }
    setSubmitting(true);
    try { await onSubmit({ evidence }); } finally { setSubmitting(false); }
  };

  const handleReview = async () => {
    if (reviewMode === 'reject' && !reason.trim()) {
      addToast?.('Rejection requires a reason for the audit trail.', 'warn');
      return;
    }
    setSubmitting(true);
    try {
      await onReview(reviewMode === 'approve' ? 'APPROVE' : 'REJECT', reason);
      setReviewMode(null);
      setReason('');
    } finally { setSubmitting(false); }
  };

  const dueIso = a.dueDate || a.due || a.deadline;
  const overdue = dueIso && new Date(dueIso).getTime() < Date.now() && (a.status || '').toUpperCase() === 'OPEN';

  return (
    <li className="bg-v2-surface border border-v2-border-subtle rounded-v2-md overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full text-left px-3 py-2.5 hover:bg-v2-muted flex items-center gap-3"
      >
        {expanded ? <ChevronDown size={14} className="text-v2-ink-muted shrink-0" /> : <ChevronRight size={14} className="text-v2-ink-muted shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-v2-ink text-[13px] truncate">
            {a.taskName || a.name || a.title || `Attestation ${String(a.id).slice(0, 8)}`}
          </div>
          <div className="text-[11px] text-v2-ink-muted truncate flex items-center gap-2">
            {a.framework && <span>{a.framework}</span>}
            {a.controlId && <><span>·</span><span className="font-mono-v2">{a.controlId}</span></>}
            {a.assignee && <><span>·</span><span>assigned to {a.assignee}</span></>}
            {dueIso && (
              <>
                <span>·</span>
                <span className={overdue ? 'text-v2-danger font-semibold' : ''}>
                  due {new Date(dueIso).toLocaleDateString()}
                </span>
              </>
            )}
          </div>
        </div>
        <StatusBadge status={a.status} />
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-v2-border-subtle bg-v2-muted/30 space-y-3">
          {a.description && (
            <p className="text-[12.5px] text-v2-ink mt-3 m-0">{a.description}</p>
          )}

          {/* Rejection reason — surfaces only when the attestation
              has been rejected so the operator can see why. */}
          {a.status?.toUpperCase() === 'REJECTED' && a.rejectionReason && (
            <div className="bg-v2-danger-soft border border-v2-danger/30 rounded-v2-sm p-2.5 text-[12px] text-v2-danger">
              <span className="font-semibold block mb-1">
                <XCircle size={11} className="inline -mt-0.5 mr-1" /> Rejection reason
              </span>
              {a.rejectionReason}
            </div>
          )}

          {/* Evidence section */}
          <div>
            <div className="text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1.5">
              Evidence ({evidence.length})
            </div>
            {loadingEvidence ? (
              <p className="text-[11.5px] text-v2-ink-muted italic m-0">Loading evidence…</p>
            ) : (
              <EvidenceUploader
                evidence={evidence}
                onUploadFile={async (file) => {
                  /* MVP: synthesise local entry (real upload requires
                   * multipart endpoint — wire when backend ships
                   * /attestations/{id}/evidence/upload). */
                  const newEntry = {
                    id: `local-${Date.now()}`,
                    filename: file.name,
                    addedAt: new Date().toISOString(),
                    _local: true,
                  };
                  setEvidence((es) => [...es, newEntry]);
                  addToast?.(`Staged ${file.name}; click Submit to commit.`, 'info');
                }}
                onAddUrl={async (url) => {
                  setEvidence((es) => [...es, {
                    id: `url-${Date.now()}`,
                    url,
                    addedAt: new Date().toISOString(),
                    _local: true,
                  }]);
                }}
                onDelete={async (id) => {
                  setEvidence((es) => es.filter((e) => e.id !== id));
                }}
                disabled={a.status?.toUpperCase() === 'APPROVED'}
              />
            )}
          </div>

          {/* Action bar — varies by view */}
          <div className="flex items-center gap-2 pt-2 border-t border-v2-border-subtle">
            {!showReviewControls && a.status?.toUpperCase() !== 'APPROVED' && (
              <Button
                variant="primary" size="sm"
                disabled={submitting || evidence.length === 0}
                onClick={handleSubmit}
              >
                {submitting ? 'Submitting…' : 'Submit evidence'}
              </Button>
            )}
            {showReviewControls && a.status?.toUpperCase() === 'SUBMITTED' && (
              <>
                {reviewMode === null && (
                  <>
                    <Button variant="primary" size="sm" iconLeft={<CheckCircle2 size={13} />}
                            onClick={() => setReviewMode('approve')}>
                      Approve
                    </Button>
                    <Button variant="danger" size="sm" iconLeft={<XCircle size={13} />}
                            onClick={() => setReviewMode('reject')}>
                      Reject
                    </Button>
                  </>
                )}
                {reviewMode !== null && (
                  <div className="flex-1 flex items-center gap-2">
                    <input
                      type="text"
                      placeholder={reviewMode === 'approve' ? 'Optional approval note' : 'Rejection reason (required)'}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className="flex-1 border border-v2-border-subtle rounded-v2-sm px-2.5 py-1.5 text-[12.5px] bg-v2-surface focus:border-v2-action focus:ring-2 focus:ring-v2-action/20 focus:outline-none"
                    />
                    <Button variant={reviewMode === 'approve' ? 'primary' : 'danger'} size="sm"
                            disabled={submitting || (reviewMode === 'reject' && !reason.trim())}
                            onClick={handleReview}>
                      {submitting ? '…' : reviewMode === 'approve' ? 'Confirm approve' : 'Confirm reject'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setReviewMode(null); setReason(''); }}>
                      Cancel
                    </Button>
                  </div>
                )}
              </>
            )}
            {a.frameworkUrl && (
              <a href={a.frameworkUrl} target="_blank" rel="noopener noreferrer"
                 className="ml-auto text-[11.5px] text-v2-action hover:underline inline-flex items-center gap-1">
                Framework reference <ExternalLink size={10} />
              </a>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

function normalizeList(r) {
  if (!r) return [];
  if (Array.isArray(r)) return r;
  return r?.data || r?.items || r?.attestations || r?.content || [];
}

export default AttestationsSlideout;
